// src/pages/MyOrders.js

import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, where, getDocs, onSnapshot, orderBy, limit, deleteDoc, doc } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import OrderModificationModal from '../components/modals/OrderModificationModal';
import OrderHistoryModal from '../components/modals/OrderHistoryModal';
import { FaEdit, FaTrash, FaInfoCircle, FaExclamationTriangle, FaBell, FaCheckCircle, FaCalendarTimes, FaClock } from 'react-icons/fa';

function MyOrders() {
  const { currentUser, userProfile, loading: authLoading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModificationModal, setShowModificationModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  // Nuevo estado para el filtro de estado
  const [filterStatus, setFilterStatus] = useState('Todos'); // 'Todos' para mostrar todos los estados

  const ordersCollectionRef = collection(db, 'pedidos');

  // Function to calculate the difference in days robustly using UTC
  const getDaysDifference = (dateString) => {
    if (!dateString || dateString === 'N/A') {
      return null; // Returns null if the date is invalid or does not exist
    }
    const today = new Date();
    // Get the start of today's day in UTC
    const todayUtc = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());

    // Parse the target date string as UTC directly to avoid timezone issues
    const parts = dateString.split('-').map(Number);
    // Add validation for the length of the parts array to avoid errors if dateString is malformed
    if (parts.length !== 3 || isNaN(parts[0]) || isNaN(parts[1]) || isNaN(parts[2])) {
      console.warn("Invalid date string for getDaysDifference:", dateString);
      return null;
    }
    const targetDateUtc = Date.UTC(parts[0], parts[1] - 1, parts[2]); // Month is 0-indexed in Date.UTC

    const diffTime = targetDateUtc - todayUtc;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); // Round up to include the current day
    return diffDays;
  };

  // Function to determine the delivery alert status based on the requested logic
  const getDeliveryAlert = (order) => {
    // Define final statuses that mean the order is no longer "active" for overdue alerts
    const finalStatuses = ['Entregado Exitosamente', 'Pedido listo para Recojo', 'Entregado en Agencia'];

    // If the order is in a final status, the alert is "Finalizado"
    if (finalStatuses.includes(order.currentStatus)) {
      return { text: 'Finalizado', color: '#20C997', icon: <FaCheckCircle /> }; // Green for finished
    }

    const daysDiff = getDaysDifference(order.expectedDeliveryDate);

    if (daysDiff < 0) {
      return { text: 'Vencido', color: '#DC3545', icon: <FaExclamationTriangle /> }; // Red for overdue
    } else if (daysDiff === 0) {
      return { text: 'Por Vencer (hoy)', color: '#FD7E14', icon: <FaBell /> }; // Orange for today
    } else { // daysDiff > 0 (1 or more days in the future)
      return { text: `Por Vencer (${daysDiff} día(s))`, color: '#FFC107', icon: <FaInfoCircle /> }; // Yellow for upcoming
    }
  };

  useEffect(() => {
    if (!authLoading && currentUser) {
      setLoading(true);
      setError('');

      let q;
      // All roles with access to "My Orders" will now see ALL orders
      if (userProfile?.rol === 'Administrador' || userProfile?.rol === 'Gerencia' || userProfile?.rol === 'Asesor' || userProfile?.rol === 'Almacen' || userProfile?.rol === 'Atención al Cliente') {
        q = collection(db, 'pedidos'); // Query to get all orders
        
        // Aplica el filtro de estado si no es 'Todos'
        if (filterStatus !== 'Todos') {
          q = query(q, where('currentStatus', '==', filterStatus));
        }
      } else {
        setError('User role not authorized to view orders.');
        setLoading(false);
        return;
      }

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const ordersData = snapshot.docs.map(doc => {
          const data = doc.data();
          
          let expectedDeliveryDateFormatted = '';
          // --- START OF DEFINITIVE DATE FIX: Robustly handle expectedDeliveryDate ---
          if (data.expectedDeliveryDate) {
            if (typeof data.expectedDeliveryDate.toDate === 'function') {
              // It's a Firestore Timestamp, convert to YYYY-MM-DD string
              expectedDeliveryDateFormatted = data.expectedDeliveryDate.toDate().toISOString().split('T')[0];
            } else if (typeof data.expectedDeliveryDate === 'string') {
              // It's already a string, use it directly
              expectedDeliveryDateFormatted = data.expectedDeliveryDate;
            }
          }
          // --- END OF DEFINITIVE DATE FIX ---

          // Robustly handle createdAt and orderDate as well
          let createdAtFormatted = 'N/A';
          if (data.createdAt) {
            if (typeof data.createdAt.toDate === 'function') {
              createdAtFormatted = data.createdAt.toDate().toISOString(); // Keep ISO format for sorting
            } else if (typeof data.createdAt === 'string') {
              createdAtFormatted = data.createdAt;
            }
          }

          let orderDateFormatted = 'N/A';
          if (data.orderDate) {
            orderDateFormatted = data.orderDate; // Assume orderDate is always a string
          } else if (data.createdAt) {
             // Fallback to createdAt if orderDate is missing, format as YYYY-MM-DD
             if (typeof data.createdAt.toDate === 'function') {
                orderDateFormatted = data.createdAt.toDate().toISOString().split('T')[0];
             } else if (typeof data.createdAt === 'string') {
                orderDateFormatted = data.createdAt.split('T')[0]; // Assuming it's an ISO string if it's a string
             }
          }

          return {
            id: doc.id,
            ...data, // Keep all other original data fields
            expectedDeliveryDate: expectedDeliveryDateFormatted,
            orderDate: orderDateFormatted,
            createdAt: createdAtFormatted // Keep as ISO string for sorting
          };
        });

        // Sorting Logic: Prioritize active orders, then sort by expected delivery date, then creation date
        const finalStatusOrders = ordersData.filter(order => ['Entregado Exitosamente'].includes(order.currentStatus));
        const otherOrders = ordersData.filter(order => !['Entregado Exitosamente'].includes(order.currentStatus));

        otherOrders.sort((a, b) => {
          // new Date() can parse 'YYYY-MM-DD' directly
          const dateA = new Date(a.expectedDeliveryDate);
          const dateB = new Date(b.expectedDeliveryDate);
          if (dateA < dateB) return -1;
          if (dateA > dateB) return 1;
          const createdAtA = new Date(a.createdAt);
          const createdAtB = new Date(b.createdAt);
          return createdAtB.getTime() - createdAtA.getTime();
        });

        finalStatusOrders.sort((a, b) => {
          const createdAtA = new Date(a.createdAt);
          const createdAtB = new Date(b.createdAt);
          return createdAtB.getTime() - createdAtA.getTime();
        });

        setOrders([...otherOrders, ...finalStatusOrders]);
        setLoading(false);
      }, (err) => {
        console.error("Error fetching real-time orders:", err);
        setError("Could not load orders. Please try again later.");
        setLoading(false);
      });

      return () => unsubscribe();
    }
  }, [authLoading, currentUser, userProfile, filterStatus]); // Añadir filterStatus como dependencia

  const handleEditClick = (order) => {
    setSelectedOrder(order);
    setShowModificationModal(true);
  };

  const handleDeleteClick = async (orderId) => {
    // IMPORTANT: In a real application, you'd want a custom confirmation modal here, not window.confirm
    const confirmDelete = window.confirm(`¿Are you sure you want to delete order ${orderId}? This action is irreversible.`);
    
    if (confirmDelete) {
      try {
        await deleteDoc(doc(db, 'pedidos', orderId));
        alert('Order deleted successfully!'); // Replace with custom modal
      } catch (err) {
        console.error("Error deleting order:", err.message);
        setError("Could not delete order: " + err.message);
      }
    }
  };

  const handleOrderUpdated = () => {
    setSelectedOrder(null);
    setShowModificationModal(false);
    // The onSnapshot listener will automatically re-fetch and update the orders state
  };

  const handleViewHistoryClick = (order) => {
    setSelectedOrder(order);
    setShowHistoryModal(true);
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }} className="app-content-container">
        Cargando pedidos...
      </div>
    );
  }

  if (error) {
    return (
      <div className="login-error app-content-container" style={{ textAlign: 'center', padding: '50px' }}>
        {error}
      </div>
    );
  }

  const canViewOrdersPage = userProfile && (
    userProfile.rol === 'Administrador' ||
    userProfile.rol === 'Gerencia' ||
    userProfile.rol === 'Asesor' ||
    userProfile.rol === 'Almacen' ||
    userProfile.rol === 'Atención al Cliente'
  );

  const canEditOrders = userProfile && (
    userProfile.rol === 'Administrador' ||
    userProfile.rol === 'Asesor' ||
    userProfile.rol === 'Almacen' ||
    userProfile.rol === 'Atención al Cliente'
  );

  const canDeleteOrders = userProfile && userProfile.rol === 'Administrador';

  // Define all possible statuses for the filter dropdown
  const allPossibleStatuses = [
    'Todos', // Option to show all orders
    'En proceso de Compra',
    'En almacén',
    'Programado para despacho',
    'En Ruta',
    'Reprogramado',
    'Entregado Exitosamente',
    'Pedido listo para Recojo',
    'Entregado en Agencia'
  ];

  if (!canViewOrdersPage) {
    return (
      <div style={{ textAlign: 'center', padding: '50px', color: 'red' }} className="app-content-container">
        <h3>Acceso Denegado</h3>
        <p>You do not have the necessary permissions to view orders.</p>
      </div>
    );
  }

  return (
    <div className="app-content-container">
      <h2 style={{ textAlign: 'center' }}>Mis Pedidos</h2>

      {/* Filtro por estado */}
      <div style={{ marginBottom: '20px', textAlign: 'center' }}>
        <label htmlFor="statusFilter" style={{ marginRight: '10px', fontWeight: 'bold' }}>Filtrar por Estado:</label>
        <select
          id="statusFilter"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          style={{
            padding: '8px 12px',
            borderRadius: '8px',
            border: '1px solid var(--border-color)',
            backgroundColor: 'white',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            minWidth: '200px',
          }}
        >
          {allPossibleStatuses.map(status => (
            <option key={status} value={status}>{status}</option>
          ))}
        </select>
      </div>

      {orders.length === 0 ? (
        <p style={{ textAlign: 'center', color: '#666' }}>No hay pedidos registrados para el estado seleccionado.</p>
      ) : (
        <div className="table-responsive">
          <table className="data-table">
            <thead>
              <tr>
                <th>Número Pedido</th>
                <th>Cliente</th>
                <th>Contacto</th>
                <th>Teléfono</th>
                <th>Dirección Entrega</th>
                <th>Fecha Pedido</th>
                <th>Fecha Prevista (General)</th>
                <th>Estado Actual</th>
                <th>Tipo de Entrega</th>
                <th>Alerta de Entrega</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => {
                const alert = getDeliveryAlert(order);

                // Simply display what's stored in order.deliveryType
                const displayDeliveryType = order.deliveryType || 'N/A';

                return (
                  <tr key={order.id}>
                    <td data-label="Número Pedido">{order.orderNumber}</td>
                    <td data-label="Cliente">{order.clientName}</td>
                    <td data-label="Contacto">{order.contactName}</td>
                    <td data-label="Teléfono">{order.contactPhone}</td>
                    <td data-label="Dirección Entrega">{order.deliveryAddress}</td>
                    <td data-label="Fecha Pedido">{order.orderDate}</td>
                    <td data-label="Fecha Prevista (General)">{order.expectedDeliveryDate}</td>
                    <td data-label="Estado Actual" style={{ color: alert.color, fontWeight: 'bold' }}>
                      {order.currentStatus}
                    </td>
                    <td data-label="Tipo de Entrega">
                      {displayDeliveryType}
                    </td>
                    <td
                      data-label="Alerta de Entrega"
                      style={{
                        color: alert.color,
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                      }}
                    >
                      {alert.icon} {alert.text}
                    </td>
                    <td data-label="Acciones" className="action-buttons">
                      {canEditOrders && (
                        <button
                          onClick={() => handleEditClick(order)}
                          style={{
                            backgroundColor: '#FFC107',
                            color: 'white',
                            fontWeight: 'bold',
                            borderRadius: '8px',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '8px 12px',
                            marginRight: '5px',
                            transition: 'background-color 0.3s ease, transform 0.2s ease',
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.05)')}
                          onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                          disabled={order.currentStatus === 'Entregado Exitosamente'}
                        >
                          <FaEdit /> Editar
                        </button>
                      )}
                      <button
                        onClick={() => handleViewHistoryClick(order)}
                        style={{
                          backgroundColor: '#17A2B8',
                          color: 'white',
                          fontWeight: 'bold',
                          borderRadius: '8px',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '8px 12px',
                          marginRight: '5px',
                          transition: 'background-color 0.3s ease, transform 0.2s ease',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                      >
                        <FaInfoCircle /> Historial
                      </button>
                      {canDeleteOrders && (
                        <button
                          onClick={() => handleDeleteClick(order.id)}
                          style={{
                            backgroundColor: '#DC3545',
                            color: 'white',
                            fontWeight: 'bold',
                            borderRadius: '8px',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '8px 12px',
                            transition: 'background-color 0.3s ease, transform 0.2s ease',
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.05)')}
                          onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                        >
                          <FaTrash /> Eliminar
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showModificationModal && selectedOrder && (
        <OrderModificationModal order={selectedOrder} onClose={() => setShowModificationModal(false)} onOrderUpdated={handleOrderUpdated} />
      )}

      {showHistoryModal && selectedOrder && (
        <OrderHistoryModal order={selectedOrder} onClose={() => setShowHistoryModal(false)} />
      )}
    </div>
  );
}

export default MyOrders;
